import {
  BELT_CHARACTERS,
  BUILDINGS,
  GAME_CONFIG,
  ItemType,
} from '../constants';
import { createGameEngine, resetGameEngine } from '../GameEngine';
import { PlayerInventory, createEmptyPlayerInventory } from '../GameState';
import { TestDefinition, TestRunner, assert } from '../TestRunner';
import { secondsToTicks } from '../utils/TickIntervals';

const MINING_INTERVAL_TICKS = secondsToTicks(3);
const PRECISION_DRILL = BUILDINGS.PRECISION_DRILL;
const PRECISION_BUFFER_CAPACITY = PRECISION_DRILL.miningBufferCapacity ?? 0;
const PRECISION_OUTPUT_PER_CYCLE = PRECISION_DRILL.miningOutputPerCycle ?? 1;

const ensureNextMiningCycleReady = (
  minerKey: string,
  minerTimerLayer: Record<string, number>
): Record<string, number> => {
  const lastTick = minerTimerLayer[minerKey] ?? 0;
  return {
    ...minerTimerLayer,
    [minerKey]: lastTick - MINING_INTERVAL_TICKS,
  };
};

const precisionDrillBufferingTest: TestDefinition = {
  name: 'Precision Drill buffers output when belts are unavailable',
  description:
    'Ensure the Precision Drill accumulates ore in its internal buffer up to its capacity when it cannot output to belts.',
  testFunction: async () => {
    resetGameEngine();

    const minerPosition = { x: 40, y: 40 };
    const minerKey = `${minerPosition.x},${minerPosition.y}`;

    const engine = createGameEngine({
      gameMap: {
        [minerKey]: GAME_CONFIG.IRON_NODE_CHARACTER,
      },
      buildingLayer: {
        [minerKey]: PRECISION_DRILL.character,
      },
      minerTimerLayer: {
        [minerKey]: -MINING_INTERVAL_TICKS,
      },
    });

    const observedBufferCounts: number[] = [];

    const totalCyclesToSimulate = PRECISION_BUFFER_CAPACITY + 1;

    for (let cycle = 0; cycle < totalCyclesToSimulate; cycle += 1) {
      engine.processTick();
      const stateAfterTick = engine.getState();
      const bufferedCount =
        stateAfterTick.minerOutputBufferLayer[minerKey] ?? 0;
      observedBufferCounts.push(bufferedCount);

      if (cycle < PRECISION_BUFFER_CAPACITY) {
        const updatedTimers = ensureNextMiningCycleReady(
          minerKey,
          stateAfterTick.minerTimerLayer
        );
        engine.setState({ minerTimerLayer: updatedTimers });
      }
    }

    for (let i = 0; i < PRECISION_BUFFER_CAPACITY; i += 1) {
      assert.equals(
        observedBufferCounts[i],
        (i + 1) * PRECISION_OUTPUT_PER_CYCLE,
        `Cycle ${i + 1} should add ${PRECISION_OUTPUT_PER_CYCLE} ore to the buffer`
      );
    }

    assert.equals(
      observedBufferCounts[PRECISION_BUFFER_CAPACITY],
      PRECISION_BUFFER_CAPACITY,
      'Buffered ore should not exceed the configured capacity'
    );

    const finalBuffer = observedBufferCounts[observedBufferCounts.length - 1];

    resetGameEngine();

    return {
      name: 'Precision Drill buffers output when belts are unavailable',
      passed: true,
      message: `Precision Drill buffered ${finalBuffer} ore when blocked`,
      duration: 0,
      details: {
        bufferedCounts: observedBufferCounts,
        capacity: PRECISION_BUFFER_CAPACITY,
      },
    };
  },
};

const precisionDrillFlushesToBeltsTest: TestDefinition = {
  name: 'Precision Drill flushes buffered ore to adjacent belts',
  description:
    'Verify that buffered ore is emitted to nearby belts when space becomes available, reducing the internal buffer.',
  testFunction: async () => {
    resetGameEngine();

    const minerPosition = { x: 32, y: 32 };
    const minerKey = `${minerPosition.x},${minerPosition.y}`;
    const beltKey = `${minerPosition.x + 1},${minerPosition.y}`;

    const engine = createGameEngine({
      gameMap: {
        [minerKey]: GAME_CONFIG.IRON_NODE_CHARACTER,
      },
      buildingLayer: {
        [minerKey]: PRECISION_DRILL.character,
      },
      minerTimerLayer: {
        [minerKey]: -MINING_INTERVAL_TICKS,
      },
    });

    for (let cycle = 0; cycle < PRECISION_BUFFER_CAPACITY; cycle += 1) {
      engine.processTick();
      const currentState = engine.getState();
      const bufferedCount = currentState.minerOutputBufferLayer[minerKey] ?? 0;

      assert.equals(
        bufferedCount,
        Math.min(cycle + 1, PRECISION_BUFFER_CAPACITY),
        'Buffered ore should increase until the capacity is reached'
      );

      if (cycle < PRECISION_BUFFER_CAPACITY - 1) {
        const updatedTimers = ensureNextMiningCycleReady(
          minerKey,
          currentState.minerTimerLayer
        );
        engine.setState({ minerTimerLayer: updatedTimers });
      }
    }

    const stateBeforeBelt = engine.getState();
    assert.equals(
      stateBeforeBelt.minerOutputBufferLayer[minerKey],
      PRECISION_BUFFER_CAPACITY,
      'Buffer should be full before adding a belt'
    );

    engine.setState({
      buildingLayer: {
        ...stateBeforeBelt.buildingLayer,
        [beltKey]: BELT_CHARACTERS.right,
      },
      beltContentLayer: {
        ...stateBeforeBelt.beltContentLayer,
        [beltKey]: [],
      },
    });

    engine.processTick();

    const postFlushState = engine.getState();
    const beltItems = postFlushState.beltContentLayer[beltKey] ?? [];
    const remainingBuffer =
      postFlushState.minerOutputBufferLayer[minerKey] ?? 0;

    assert.equals(
      beltItems.length,
      1,
      'Precision Drill should output a single buffered ore onto the adjacent belt'
    );

    const beltItemType = beltItems[0]?.item as ItemType | undefined;
    assert.equals(
      beltItemType,
      'iron-ore',
      'Buffered output should place iron ore on the belt'
    );

    assert.equals(
      remainingBuffer,
      Math.max(0, PRECISION_BUFFER_CAPACITY - PRECISION_OUTPUT_PER_CYCLE),
      'Internal buffer should decrease by the amount moved to the belt'
    );

    resetGameEngine();

    return {
      name: 'Precision Drill flushes buffered ore to adjacent belts',
      passed: true,
      message:
        'Precision Drill emitted buffered ore to the belt and reduced its buffer.',
      duration: 0,
      details: {
        beltItems: beltItems.map(item => ({
          item: item.item,
          progress: item.progress,
        })),
        remainingBuffer,
      },
    };
  },
};

const precisionDrillPartialCollectionTest: TestDefinition = {
  name: 'Precision Drill manual collection respects inventory limits',
  description:
    'Confirm that manually collecting from a Precision Drill with a full player inventory transfers only available space and leaves the remainder buffered.',
  testFunction: async () => {
    resetGameEngine();

    const minerPosition = { x: 28, y: 28 };
    const minerKey = `${minerPosition.x},${minerPosition.y}`;

    const constrainedInventory: PlayerInventory = createEmptyPlayerInventory();
    constrainedInventory[0] = { itemType: 'iron-ore', count: 48 };
    for (let i = 1; i < constrainedInventory.length; i += 1) {
      constrainedInventory[i] = { itemType: 'iron-plate', count: 50 };
    }

    const engine = createGameEngine({
      gameMap: {
        [minerKey]: GAME_CONFIG.IRON_NODE_CHARACTER,
      },
      buildingLayer: {
        [minerKey]: PRECISION_DRILL.character,
      },
      minerOutputBufferLayer: {
        [minerKey]: PRECISION_BUFFER_CAPACITY,
      },
      minerTimerLayer: {
        [minerKey]: 0,
      },
      playerInventory: constrainedInventory,
    });

    const result = engine.collectBuildingInventory(minerPosition);
    const updatedState = engine.getState();
    const updatedInventory = updatedState.playerInventory;
    const remainingBuffer = updatedState.minerOutputBufferLayer[minerKey] ?? 0;

    const addedIronOre = result.addedItems['iron-ore'] ?? 0;

    assert.isTrue(
      result.success,
      'Manual collection should succeed when buffer has ore'
    );
    assert.isTrue(
      Boolean(result.partial),
      'Collection should report a partial transfer due to limited inventory space'
    );
    assert.equals(
      addedIronOre,
      2,
      'Exactly two iron ore should fit into the player inventory stack'
    );
    assert.equals(
      updatedInventory[0]?.count,
      50,
      'Player inventory stack should be filled to its maximum size after collection'
    );
    assert.equals(
      remainingBuffer,
      PRECISION_BUFFER_CAPACITY - addedIronOre,
      'Buffer should retain the uncollected ore after partial manual collection'
    );

    resetGameEngine();

    return {
      name: 'Precision Drill manual collection respects inventory limits',
      passed: true,
      message: `Collected ${addedIronOre} iron ore while leaving ${remainingBuffer} buffered`,
      duration: 0,
      details: {
        addedIronOre,
        remainingBuffer,
      },
    };
  },
};

export const precisionDrillTests: TestDefinition[] = [
  precisionDrillBufferingTest,
  precisionDrillFlushesToBeltsTest,
  precisionDrillPartialCollectionTest,
];

export async function runPrecisionDrillTests(): Promise<void> {
  const runner = new TestRunner();
  precisionDrillTests.forEach(test => runner.addTest(test));
  await runner.runAllTests();
}
